import { PoliciesBaseDataModel } from '@/features/project/_models/filter-base-data';
import { t } from '@/i18n';
import type { booleanStringType } from '@/utils';
import { z } from 'zod';

export const PublishedLabelModifier = {
  true: t('published'),
  false: t('not-published'),
} as const satisfies Record<z.infer<typeof booleanStringType>, string>;

export const NestedPoliciesModel = z.object({
  result: PoliciesBaseDataModel,
  projectCount: z.number(),
});

export type ProjectTableColumn =
  | 'name'
  | 'ministryName'
  | 'projectNumber'
  | 'startYear'
  | 'budgetData'
  | 'finalizedAmountForInitialBudget'
  | 'organizationLabel'
  | 'endYear'
  | 'purpose'
  | 'task'
  | 'previousYearExecutedAmounts'
  | 'previousYearExecutionRate'
  | 'nextYearRequestedAmount'
  | 'finalizedAmountForSupplementaryBudget'
  | 'fundOwner'
  | 'fiscalYearOfCreation'
  | 'endDateOfNewApplicationReceiving'
  | 'scheduledEndDate'
  | 'administrativeExpenses'
  | 'previousYearTotalIncome'
  | 'previousYearTotalExpense'
  | 'amountReturnedToTheNationalTreasury'
  | 'fundSheetNumber'
  | 'fundSheetBranchNumber'
  | 'operationForms'
  | 'businessForms'
  | 'beginningOfTheFundBalance'
  | 'ownershipRatio'
  | 'isPublished'
  | 'inspectionByExternalExpertReviewType'
  | 'candidateReasonsOfInspectionByExternalExpert'
  | 'mainPayment'
  | 'paymentAmount'
  | 'previousYearAdministrativeExpenseRate'
  | 'previousYearDivergenceRate'
  | 'lastImplementedFiscalYear';

// TODO change label on change translation
export const ProjectTableColumnLabelModifier = {
  name: t('project'),
  ministryName: t('ministry'),
  projectNumber: t('project-number'),
  startYear: t('start-fiscal-year'),
  budgetData: t('budget-graph'),
  finalizedAmountForInitialBudget: t('initial-finalized-amount'),
  organizationLabel: t('organization-in-charge'),
  endYear: t('end-fiscal-year'),
  purpose: t('project-purpose'),
  task: t('project-task'),
  previousYearExecutedAmounts: t('previous-fiscal-year-executed'),
  previousYearExecutionRate: t('previous-year-execution-rate'),
  nextYearRequestedAmount: t('next-request-amount'),
  finalizedAmountForSupplementaryBudget: t('supplementary-finalized-amount'),
  fundOwner: t('fund-owner'),
  fiscalYearOfCreation: t('fiscal-year-of-creation'),
  endDateOfNewApplicationReceiving: t('end-date-of-new-application'),
  scheduledEndDate: t('fund-scheduled-end-period'),
  administrativeExpenses: t('previous-year-of-administrative-expenses'),
  previousYearTotalIncome: t('previous-year-of-total-income'),
  previousYearTotalExpense: t(
    'previous-year-of-total-expense-thousand-yen-unit',
  ),
  amountReturnedToTheNationalTreasury: t(
    'previous-year-of-amount-of-national-treasury-return',
  ),
  fundSheetNumber: t('fund-sheet-number'),
  fundSheetBranchNumber: t('fund-sheet-branch-number'),
  operationForms: t('operation-form'),
  businessForms: t('business-form'),
  beginningOfTheFundBalance: t(
    'fund-balance-at-the-end-of-previous-fiscal-year',
  ),
  ownershipRatio: t('ownership-ratio'),
  isPublished: t('publish-ability'),
  inspectionByExternalExpertReviewType: t('inspection-target'),
  candidateReasonsOfInspectionByExternalExpert: t(
    'candidate-reasons-of-inspection-by-external-expert',
  ),
  mainPayment: t('main-payment'),
  paymentAmount: t('amount-of-expenditures'),
  previousYearAdministrativeExpenseRate: t(
    'previous-year-of-administrative-expenses-rate',
  ),
  previousYearDivergenceRate: t('previous-year-of-divergence-rate'),
  lastImplementedFiscalYear: t('projects.last-implemented-fiscal-year'),
} satisfies Record<ProjectTableColumn, string>;
